import type { AisPointCollection, AisVesselProperties } from "./aisData";
import type { RoutePoint } from "../routes/routes";

const EARTH_RADIUS_METRES = 6_371_008.8;
const KNOTS_TO_METRES_PER_SECOND = 0.514_444;

export const BASE_COLLISION_CLEARANCE_METRES = 30;
export const COLLISION_PROJECTION_MINUTES = 20;

export interface TypicalCourse {
  courseDeg: number;
  confidence: number;
  sampleCount: number;
}

export interface OwnVesselProjection {
  position: RoutePoint;
  courseDeg: number;
  speedKnots: number;
  maxSeconds?: number;
  ownMmsi?: string;
}

export interface CollisionRisk {
  vessel: AisVesselProperties;
  closestApproachMetres: number;
  minutesToClosestApproach: number;
  safetyLimitMetres: number;
  projectedCourseDeg: number;
  typicalCourseUsed: boolean;
}

function validCourse(value: number | undefined): value is number {
  return value !== undefined && Number.isFinite(value) && value >= 0 && value < 360;
}

function usableCourse(properties: AisVesselProperties): number | null {
  return properties.course_source !== "unavailable" && validCourse(properties.course_deg)
    ? properties.course_deg
    : null;
}

function circularMean(courses: number[]): TypicalCourse | null {
  if (!courses.length) return null;
  let east = 0;
  let north = 0;
  for (const course of courses) {
    const radians = course * Math.PI / 180;
    east += Math.sin(radians);
    north += Math.cos(radians);
  }
  const magnitude = Math.hypot(east, north);
  if (magnitude < 0.01) return null;
  return {
    courseDeg: (Math.atan2(east, north) * 180 / Math.PI + 360) % 360,
    confidence: Math.min(1, magnitude / courses.length),
    sampleCount: courses.length,
  };
}

/** Circular historical mean by MMSI; opposing historical routes lower confidence. */
export function typicalCoursesByVessel(
  collection: AisPointCollection | null,
): Map<string, TypicalCourse> {
  const samples = new Map<string, number[]>();
  for (const feature of collection?.features ?? []) {
    const course = usableCourse(feature.properties);
    if (course === null) continue;
    const vesselCourses = samples.get(feature.properties.id) ?? [];
    vesselCourses.push(course);
    samples.set(feature.properties.id, vesselCourses);
  }
  const result = new Map<string, TypicalCourse>();
  for (const [id, courses] of samples) {
    const typical = circularMean(courses);
    if (typical) result.set(id, typical);
  }
  return result;
}

function blendedCourse(current: number, typical: TypicalCourse | undefined): number {
  if (!typical || typical.sampleCount < 2 || typical.confidence < 0.2) return current;
  // Current COG describes a manoeuvre better than history. Historical traffic
  // is deliberately only a modest stabilising influence on noisy/sparse fixes.
  const typicalWeight = 0.25 * typical.confidence;
  const currentRadians = current * Math.PI / 180;
  const typicalRadians = typical.courseDeg * Math.PI / 180;
  const east = Math.sin(currentRadians) * (1 - typicalWeight)
    + Math.sin(typicalRadians) * typicalWeight;
  const north = Math.cos(currentRadians) * (1 - typicalWeight)
    + Math.cos(typicalRadians) * typicalWeight;
  return (Math.atan2(east, north) * 180 / Math.PI + 360) % 360;
}

function velocity(courseDeg: number, speedKnots: number): [number, number] {
  const radians = courseDeg * Math.PI / 180;
  const speed = speedKnots * KNOTS_TO_METRES_PER_SECOND;
  return [Math.sin(radians) * speed, Math.cos(radians) * speed];
}

function relativeMetres(origin: RoutePoint, point: RoutePoint): [number, number] {
  const meanLatitude = (origin[1] + point[1]) * Math.PI / 360;
  return [
    (point[0] - origin[0]) * Math.PI / 180 * EARTH_RADIUS_METRES * Math.cos(meanLatitude),
    (point[1] - origin[1]) * Math.PI / 180 * EARTH_RADIUS_METRES,
  ];
}

function vesselSafetyLimit(properties: AisVesselProperties): number {
  const length = properties.length_metres !== undefined &&
    Number.isFinite(properties.length_metres) && properties.length_metres > 0
    ? properties.length_metres
    : 0;
  const width = properties.width_metres !== undefined &&
    Number.isFinite(properties.width_metres) && properties.width_metres > 0
    ? properties.width_metres
    : 0;
  if (!length && !width) return BASE_COLLISION_CLEARANCE_METRES;
  // AIS coordinates represent an antenna/reference point, not the hull edge.
  return BASE_COLLISION_CLEARANCE_METRES + Math.hypot(length, width) / 2;
}

export function collisionRisks(
  own: OwnVesselProjection,
  traffic: AisPointCollection | null,
  typicalCourses: Map<string, TypicalCourse>,
  nowSeconds = Date.now() / 1000,
): CollisionRisk[] {
  if (
    !traffic || !validCourse(own.courseDeg) ||
    !Number.isFinite(own.speedKnots) || own.speedKnots < 0.3
  ) return [];

  const ownVelocity = velocity(own.courseDeg, own.speedKnots);
  const horizon = Math.max(
    0,
    Math.min(COLLISION_PROJECTION_MINUTES * 60, own.maxSeconds ?? Number.POSITIVE_INFINITY),
  );
  if (!horizon) return [];
  const risks: CollisionRisk[] = [];

  for (const feature of traffic.features) {
    const properties = feature.properties;
    if (own.ownMmsi && properties.mmsi === own.ownMmsi) continue;
    const speedKnots = properties.speed_knots;
    if (speedKnots === null || speedKnots === undefined || !Number.isFinite(speedKnots)) continue;
    const reportedCourse = usableCourse(properties);
    const typical = typicalCourses.get(properties.id);
    const usableTypical = typical && typical.sampleCount >= 2 && typical.confidence >= 0.2
      ? typical
      : undefined;
    if (reportedCourse === null && speedKnots >= 0.3 && !usableTypical) continue;
    const course = reportedCourse === null
      ? usableTypical?.courseDeg ?? 0
      : blendedCourse(reportedCourse, usableTypical);
    const targetVelocity = speedKnots < 0.3 ? [0, 0] as [number, number] : velocity(course, speedKnots);
    const position = relativeMetres(own.position, feature.geometry.coordinates as RoutePoint);
    const reportedAgeSeconds = nowSeconds - properties.timestamp;
    if (reportedAgeSeconds > 15 * 60) continue;
    const ageSeconds = Math.max(0, reportedAgeSeconds);
    position[0] += targetVelocity[0] * ageSeconds;
    position[1] += targetVelocity[1] * ageSeconds;

    const relativeVelocity: [number, number] = [
      targetVelocity[0] - ownVelocity[0],
      targetVelocity[1] - ownVelocity[1],
    ];
    const relativeSpeedSquared = relativeVelocity[0] ** 2 + relativeVelocity[1] ** 2;
    const rawClosestTime = relativeSpeedSquared > 0.0001
      ? -(position[0] * relativeVelocity[0] + position[1] * relativeVelocity[1])
        / relativeSpeedSquared
      : 0;
    const closestTime = Math.max(0, Math.min(horizon, rawClosestTime));
    const closestX = position[0] + relativeVelocity[0] * closestTime;
    const closestY = position[1] + relativeVelocity[1] * closestTime;
    const closestApproachMetres = Math.hypot(closestX, closestY);
    const safetyLimitMetres = vesselSafetyLimit(properties);
    if (closestApproachMetres > safetyLimitMetres) continue;
    // A closest point beyond the forecast window is not an imminent warning.
    if (rawClosestTime > horizon || (rawClosestTime < 0 && Math.hypot(...position) > safetyLimitMetres)) {
      continue;
    }
    risks.push({
      vessel: properties,
      closestApproachMetres,
      minutesToClosestApproach: closestTime / 60,
      safetyLimitMetres,
      projectedCourseDeg: course,
      typicalCourseUsed: Boolean(usableTypical),
    });
  }

  return risks.sort((left, right) =>
    left.minutesToClosestApproach - right.minutesToClosestApproach ||
    left.closestApproachMetres - right.closestApproachMetres
  );
}
